import { Typography, useTheme } from "@mui/material";
import Modal from "components/Common/Modal";
import { useEffect, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import GeneralTermsCondition from "components/GTC/GeneralTermsConditions";
import {
  marketplace,
  useSubmitGtcMutation,
  useGetGtcApprovalStatsQuery,
} from "api/api-marketplace";
import { useGetCurrentUserQuery } from "api/api-users-v2";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { toast } from "react-toastify";
import { Form, Formik } from "formik";
import CheckboxField from "components/Common/FormFields/CheckboxField";
import {
  GTC_ACCEPT_BUTTON_LABEL,
  GTC_CONSENT_LABEL,
  GTC_SCROLL_ROOT_ID,
  GTC_SCROLL_TO_ACCEPT_HINT,
  GTC_TYPE_SUPPLIER,
} from "constants/gtc";
import { persistGtcApproval } from "helpers/gtcApprovalStorage";
import { isInternalRole } from "helpers/normalizeRoleName";
import { resolveGtcType } from "helpers/gtcType";
import { canRequestGtcPrompt } from "helpers/gtcPromptEligibility";
import { useReachedDocumentEnd } from "hooks/useReachedDocumentEnd";

const GtcApprovalModal = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const USER_ROLE = useAppSelector((state) => state.appState.role);
  const profile = useAppSelector((state) => state.appState.profile);
  const { data: currentUser } = useGetCurrentUserQuery();
  const accountId = USER_ROLE?.id ?? "";
  const gtcType = resolveGtcType(profile);
  const effectiveGtcType = gtcType ?? GTC_TYPE_SUPPLIER;
  const isInternalUser =
    currentUser?.is_internal === true || isInternalRole(currentUser?.user_role);
  const canPrompt =
    Boolean(currentUser) &&
    Boolean(accountId) &&
    currentUser?.email_verified === true &&
    canRequestGtcPrompt(accountId, effectiveGtcType, isInternalUser);
  const { data: gtcApprovalStats, isFetching } = useGetGtcApprovalStatsQuery(
    { accountId, gtcType: effectiveGtcType },
    {
      skip: !canPrompt,
    },
  );
  const [submitGtc] = useSubmitGtcMutation();
  const [showModal, setShowModal] = useState(false);
  const documentEndRef = useRef<HTMLDivElement>(null);
  const hasReachedEnd = useReachedDocumentEnd(documentEndRef, showModal);

  useEffect(() => {
    if (!canPrompt || isFetching) {
      setShowModal(false);
      return;
    }
    if (gtcApprovalStats === true) {
      persistGtcApproval(accountId, effectiveGtcType);
      setShowModal(false);
      return;
    }
    if (gtcApprovalStats === false) {
      setShowModal(true);
    }
  }, [accountId, canPrompt, effectiveGtcType, gtcApprovalStats, isFetching]);

  const blockClose = () => undefined;

  const action = () => {
    if (!accountId) {
      return;
    }
    submitGtc({ id: accountId, gtcType: effectiveGtcType })
      .unwrap()
      .then(() => {
        persistGtcApproval(accountId, effectiveGtcType);
        dispatch(
          marketplace.util.updateQueryData(
            "getGtcApprovalStats",
            { accountId, gtcType: effectiveGtcType },
            () => true,
          ),
        );
        setShowModal(false);
      })
      .catch((error) => {
        toast.error(`Failed to submit GTC approval ${error?.message}`);
      });
  };
  const modalOverlaySx = {
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "2ch",
    padding: "2ch",
    fontSize: `${theme.typography.fontSize}px !important`,
    fontFamily: `${theme.typography.fontFamily} !important`,
    height: "inherit !important",
    fontWeight: "inherit !important",
  };
  const modalSx = {
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    "&:focus-visible": {
      outline: "none",
    },
  };
  return (
    <Formik initialValues={{ accept: false }} onSubmit={() => {}}>
      {(formikProps) => {
        return (
          <Form>
            <Modal
              message={
                <>
                  <GeneralTermsCondition />
                  <div ref={documentEndRef} data-testid="gtc-document-end" />
                </>
              }
              messageContainerProps={{ id: GTC_SCROLL_ROOT_ID }}
              sx={modalSx}
              width={isMobile ? "90%" : "60%"}
              height="80vh"
              open={showModal}
              modalOverlaySx={modalOverlaySx}
              modalFooterSx={{ maxWidth: "100%" }}
              onClose={blockClose}
              closeButton={false}
              modalContentSx={{ padding: "8px !important" }}
              modalHeaderSx={{ backgroundColor: "transparent" }}
              primary={
                hasReachedEnd
                  ? {
                      children: GTC_ACCEPT_BUTTON_LABEL,
                      onClick: action,
                      disabled: !formikProps.values.accept,
                    }
                  : undefined
              }
              footer={
                hasReachedEnd ? (
                  <CheckboxField name="accept" label={GTC_CONSENT_LABEL} />
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ px: 2, py: 1 }}
                  >
                    {GTC_SCROLL_TO_ACCEPT_HINT}
                  </Typography>
                )
              }
            />
          </Form>
        );
      }}
    </Formik>
  );
};

export default GtcApprovalModal;
